/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import { Mail, Lock, Loader2, Eye, EyeOff, ArrowLeft, User, Phone } from "lucide-react";
import { toast } from "react-hot-toast";
import api from "../services/api";
import { EMAIL_ONLY_SIGNUP } from "../constants/signup";

const COMMUNITY_URL = "https://community.salescaptain.com/";

const McpLogin = () => {
  const [view, setView] = useState("login"); // login | signup | forgot
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [countdown, setCountdown] = useState(10);
  const visitorInfoRef = useRef({ ip: '', country: '' });

  const resetForm = () => {
    setName("");
    setEmail("");
    setPhone("");
    setPassword("");
    setShowPassword(false);
    setEmailSent(false);
    setSubmitted(false);
    setCountdown(10);
  };

  const switchView = (nextView) => {
    resetForm();
    setView(nextView);
  };

  const redirectToAuthorize = (response) => {
    const urlParams = new URLSearchParams(window.location.search);
    const authorizeParams = new URLSearchParams({
      client_id: urlParams.get("client_id") || "",
      redirect_uri: urlParams.get("redirect_uri") || "",
      response_type: urlParams.get("response_type") || "",
      state: urlParams.get("state") || "",
      user_id: response?.data?.user?.user_id ?? response?.data?.user_id ?? "",
      access_token: response.data.access_token,
    });

    const codeChallenge = urlParams.get("code_challenge");
    const codeChallengeMethod = urlParams.get("code_challenge_method");
    if (codeChallenge) authorizeParams.set("code_challenge", codeChallenge);
    if (codeChallengeMethod) authorizeParams.set("code_challenge_method", codeChallengeMethod);

    const mcpAuthorizeUrl = process.env.REACT_APP_MCP_AUTHORIZE_URL;
    window.location.href = `${mcpAuthorizeUrl}?${authorizeParams.toString()}`;
  };

  const handleLogin = async (e) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const response = await api.login({ email, password });
      if (response.status === "success") {
        toast.success(response.message);
        redirectToAuthorize(response);
      } else {
        toast.error(response.message);
      }
    } catch (error) {
      toast.error("Login failed");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleSignUp = async (e) => {
    e.preventDefault();

    if (EMAIL_ONLY_SIGNUP && (!name || !email || !phone)) {
      toast.error("Please fill in all fields");
      return;
    }

    if (!EMAIL_ONLY_SIGNUP && !email) {
      toast.error("Please enter your email address");
      return;
    }

    if (!EMAIL_ONLY_SIGNUP && !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      const response = EMAIL_ONLY_SIGNUP
        ? (
            await Promise.all([
              new Promise((resolve) => setTimeout(resolve, 600)),
              api.signupWithEmail({
                name,
                email,
                phone,
                ip: visitorInfoRef.current.ip,
                country: visitorInfoRef.current.country,
              }),
            ])
          )[1]
        : await api.signup({ email, password });

      if (response.status === "success") {
        if (EMAIL_ONLY_SIGNUP) {
          setSubmitted(true);
        } else {
          toast.success(response.message || "Account created successfully");
          switchView("login");
        }
      } else {
        toast.error(response.message || "Failed to create account");
      }
    } catch (error) {
      toast.error("Failed to create account");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();

    if (!email) {
      toast.error("Please enter your email address");
      return;
    }

    const regEx = /[a-zA-Z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,8}(.[a-z{2,8}])?/g;
    if (!regEx.test(email)) {
      toast.error("Please enter a valid email address");
      return;
    }

    setLoading(true);
    try {
      const response = await api.forgotPassword({ email });
      if (response.status === "success") {
        toast.success(response.message || "Password reset instructions sent to your email");
        setEmailSent(true);
      } else {
        toast.error(response.message || "Failed to send reset instructions");
      }
    } catch (error) {
      toast.error("Failed to send reset instructions");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  useEffect(() => {
    api.getVisitorInfo().then((info) => {
      visitorInfoRef.current = info;
    });
  }, []);

  useEffect(() => {
    if (!submitted) return undefined;

    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          window.location.href = "https://salescaptain.com/";
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [submitted]);

  const titles = {
    login: {
      heading: "Welcome to SalesCaptain",
      sub: "Log in to your SalesCaptain account",
    },
    signup: {
      heading: "Create your account",
      sub: "Sign up for a SalesCaptain account",
    },
    forgot: {
      heading: "Forgot Password",
      sub: "Enter the email address you registered with and we will email you instructions to reset your password.",
    },
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white p-4 sm:p-6">
      <div className="w-full max-w-md">
        <div className="flex justify-center mb-10">
          <img
            src="https://s3.us-east-2.amazonaws.com/sc-prod-embedded.salescaptain.com/resources/2025-03-18/full_logo.a46683a620fa53c3c554_1742299090688.png"
            alt="SalesCaptain Logo"
            className="h-10 object-contain"
          />
        </div>

        {submitted ? (
          <div className="text-center space-y-6">
            <h1 className="text-2xl font-semibold mb-2">Thank you</h1>
            <p className="text-gray-500">
              Our sales team will contact you shortly.
            </p>
            <p className="text-gray-500 text-sm">
              Redirecting to our website in {countdown} second{countdown === 1 ? "" : "s"}...
            </p>
            <button
              type="button"
              onClick={() => {
                window.location.href = "https://salescaptain.com/";
              }}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
            >
              Click to redirect now
            </button>
          </div>
        ) : (
          <>
        <h1 className="text-2xl font-semibold text-center mb-2">
          {titles[view].heading}
        </h1>
        <p className="text-gray-500 text-center mb-8">{titles[view].sub}</p>

        {view === "forgot" && emailSent ? (
          <div className="space-y-6">
            <p className="text-gray-600 text-center text-sm">
              We have sent you an email with instructions to reset your password.
              If you don't see it in your inbox, please check your spam folder.
            </p>
            <button
              type="button"
              onClick={() => switchView("login")}
              className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white rounded-lg"
            >
              Back to Sign In
            </button>
          </div>
        ) : (
          <form
            onSubmit={
              view === "login"
                ? handleLogin
                : view === "signup"
                  ? handleSignUp
                  : handleForgotPassword
            }
            className="space-y-4"
          >
            {view === "signup" && EMAIL_ONLY_SIGNUP && (
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Full name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="pl-10 h-12 w-full border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                  required
                  disabled={loading}
                />
              </div>
            )}

            <div className="relative">
              <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 h-5 w-5" />
              <input
                type="email"
                placeholder="Email address"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="pl-10 h-12 w-full border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                required
                disabled={loading}
              />
            </div>

            {view === "signup" && EMAIL_ONLY_SIGNUP && (
              <div className="relative">
                <Phone className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 h-5 w-5" />
                <input
                  type="tel"
                  placeholder="Phone number"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="pl-10 h-12 w-full border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none disabled:opacity-60 disabled:cursor-not-allowed"
                  required
                  disabled={loading}
                />
              </div>
            )}

            {(view === "login" || (view === "signup" && !EMAIL_ONLY_SIGNUP)) && (
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 h-5 w-5" />
                <input
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="pl-10 h-12 w-full border border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                  required
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 h-5 w-5"
                >
                  {showPassword ? (
                    <Eye className="h-5 w-5" />
                  ) : (
                    <EyeOff className="h-5 w-5" />
                  )}
                </button>
              </div>
            )}

            {view === "login" && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={() => switchView("forgot")}
                  className="text-sm text-blue-600 hover:text-blue-800"
                >
                  Forgot password?
                </button>
              </div>
            )}

            <button
              type="submit"
              className={`w-full h-12 bg-blue-600 text-white rounded-lg flex items-center justify-center ${
                loading
                  ? "opacity-60 cursor-not-allowed"
                  : "hover:bg-blue-700"
              }`}
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin mr-2" />
                  {view === "login"
                    ? "Signing in..."
                    : view === "signup"
                      ? "Creating account..."
                      : "Sending..."}
                </>
              ) : view === "login" ? (
                "Sign In"
              ) : view === "signup" ? (
                "Sign Up"
              ) : (
                "Send Instructions"
              )}
            </button>

            {view === "login" && (
              <button
                type="button"
                onClick={() => {
                  window.location.href = COMMUNITY_URL;
                }}
                className="w-full h-12 border border-gray-200 rounded-lg hover:bg-gray-50 text-gray-700"
                disabled={loading}
              >
                Cancel
              </button>
            )}
          </form>
        )}

        {view === "login" && (
          <div className="mt-8 text-center text-sm">
            <p className="text-gray-500">
              Don't have an account?{" "}
              <button
                type="button"
                onClick={() => switchView("signup")}
                className="text-blue-600 hover:text-blue-800"
              >
                Sign Up
              </button>
            </p>
          </div>
        )}

        {(view === "signup" || view === "forgot") && !emailSent && (
          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={() => switchView("login")}
              className="inline-flex items-center text-blue-600 hover:text-blue-800 text-sm"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Back to Sign In
            </button>
          </div>
        )}

        {view === "signup" && (
          <div className="mt-4 text-center text-sm">
            <p className="text-gray-500">
              Already have an account?{" "}
              <button
                type="button"
                onClick={() => switchView("login")}
                className="text-blue-600 hover:text-blue-800"
              >
                Sign In
              </button>
            </p>
          </div>
        )}
          </>
        )}
      </div>
    </div>
  );
};

export default McpLogin;
